/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 調査詳細の操作群（緊急度ドロップダウン + アクション行 + エスカレーション確認 + 解除フィードバック）。ops.requests.$id 本体から切り出したコンポーネントで（状態変更は props の callback 経由、補足対応の起票フォームへの遷移だけは自前で行う）、ゲート（editable / canEscalate / canChangeUrgency）と i18n は呼び出し元と同一に保つ。完了済みのダッシュボード起票の依頼には「補足対応を作成」を出し、元依頼を引き継いだ起票フォームへ移る
 * @graph-connects OpsDetailPage [references] 依頼詳細ページ本体からアクション UI を分離
 * @graph-connects canCreateOpsFollowUp [calls] 補足対応を作成できる依頼か判定
 */
import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useNavigate } from '@tanstack/react-router';
import { canCreateOpsFollowUp } from '@cortex/ops-core';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Select, type SelectOption } from '@cortex/web-design-system/components/ui/Select';
import { AccentBox } from './OpsPanels.js';
import type { OpsRequest, OpsState, OpsUrgency } from '../../../types/ops.js';

/** 緊急度ドロップダウンの props。 */
export interface OpsUrgencySelectProps {
  /** 表示対象の依頼（urgency を参照）。 */
  request: OpsRequest;
  /** Firestore 発番（編集可能）依頼かどうか。false のとき非表示。 */
  editable: boolean;
  /** 緊急度変更可否（起票者 or 担当者）。 */
  canChangeUrgency: boolean;
  /** 緊急度の選択肢。 */
  urgencyOptions: SelectOption[];
  /** 緊急度変更 PATCH 実行中か。 */
  isUrgencyPending: boolean;
  /** 緊急度を適用する。 */
  onApplyUrgency: (next: OpsUrgency) => void;
  /** 翻訳関数。 */
  t: TFunction;
}

/** ops 調査詳細のアクション群の props。 */
export interface OpsDetailActionsProps {
  /** 依頼 id（補足対応の起票フォームへ元依頼として渡す）。 */
  requestId: string;
  /** Firestore 発番（編集可能）依頼かどうか。false=legacy 読み取り専用。 */
  editable: boolean;
  /** 現在の進捗ステート（closed のとき解決ボタンを無効化）。 */
  state: OpsState;
  /** エスカレーション可否（担当エンジニアのみ）。 */
  canEscalate: boolean;
  /** 既にエスカレーション中か（request.escalated。urgency とは独立。ボタンを De-escalate にトグル）。 */
  isEscalated: boolean;
  /** エスカレーション確認を開いているか。 */
  confirmingEscalate: boolean;
  /** 確認を開く（前回の成否表示を reset してから開く）。 */
  onOpenConfirm: () => void;
  /** 確認の開閉（キャンセル時の close に使う）。 */
  setConfirmingEscalate: (value: boolean) => void;
  /** ステート変更 PATCH 実行中か（解決ボタンの disable 用）。 */
  isStatePending: boolean;
  /** エスカレーション実行中か。 */
  isEscalatePending: boolean;
  /** エスカレーション解除実行中か。 */
  isDeescalatePending: boolean;
  /** エスカレーションが成功したか。 */
  isEscalateSuccess: boolean;
  /** エスカレーションが失敗したか。 */
  isEscalateError: boolean;
  /** エスカレーション解除が成功したか。 */
  isDeescalateSuccess: boolean;
  /** エスカレーション解除が失敗したか。 */
  isDeescalateError: boolean;
  /** ステートを適用する（解決ボタン）。 */
  onApplyState: (next: OpsState) => void;
  /** エスカレーションを確定する。 */
  onEscalateConfirmed: () => void;
  /** エスカレーションを解除する。 */
  onDeescalate: () => void;
  /** バグ起票モーダルを開く。 */
  onCreateBugTicket: () => void;
  /** 既にバグチケットが紐付いているか（起票ボタンを無効化する）。 */
  bugTicketExists: boolean;
  /** 差戻しモーダルを開く（依頼者へ追加情報を依頼）。state!=='blocked' のとき表示。 */
  onSendBack: () => void;
  /** 翻訳関数。 */
  t: TFunction;
}

/**
 * 緊急度ドロップダウン（ヘッダ内）。editable のときだけ描画する。
 * @graph-connects none
 */
export function OpsUrgencySelect({
  request,
  editable,
  canChangeUrgency,
  urgencyOptions,
  isUrgencyPending,
  onApplyUrgency,
  t,
}: OpsUrgencySelectProps): ReactNode {
  if (!editable) return null;
  return (
    <div className="w-40" title={canChangeUrgency ? undefined : t('ops.detail.urgencyChangeHint')}>
      <Select
        aria-label={t('ops.detail.urgencyAria')}
        options={urgencyOptions}
        value={request.urgency}
        onChange={(e) => {
          onApplyUrgency(e.target.value as OpsUrgency);
        }}
        disabled={!canChangeUrgency || isUrgencyPending}
        fullWidth
        size="sm"
      />
    </div>
  );
}

/**
 * アクション行 + エスカレーション確認 + 解除フィードバック。
 * @graph-connects none
 */
export function OpsDetailActions({
  requestId,
  editable,
  state,
  canEscalate,
  isEscalated,
  confirmingEscalate,
  onOpenConfirm,
  setConfirmingEscalate,
  isStatePending,
  isEscalatePending,
  isDeescalatePending,
  isEscalateSuccess,
  isEscalateError,
  isDeescalateSuccess,
  isDeescalateError,
  onApplyState,
  onEscalateConfirmed,
  onDeescalate,
  onCreateBugTicket,
  bugTicketExists,
  onSendBack,
  t,
}: OpsDetailActionsProps): ReactNode {
  const navigate = useNavigate();
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {/* 依頼レコードを書き換えるアクションは legacy（読み取り専用）では無効化する。 */}
        <Button
          type="button"
          variant="primary"
          size="sm"
          disabled={!editable || state === 'closed' || isStatePending}
          onClick={() => {
            onApplyState('closed');
          }}
        >
          {t('ops.detail.markResolved')}
        </Button>
        {/* 完了済みの依頼に続けて追加の対応を頼む。元依頼を引き継いだ起票フォームへ移る（閲覧できる人なら誰でも）。 */}
        {canCreateOpsFollowUp({ id: requestId, state }) && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              void navigate({ to: '/ops/submit', search: { followUpOf: requestId } });
            }}
          >
            {t('ops.detail.followUp.create')}
          </Button>
        )}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={!editable || bugTicketExists}
          title={bugTicketExists ? t('ops.detail.bugTicket.alreadyExists') : undefined}
          onClick={onCreateBugTicket}
        >
          {t('ops.detail.createBugTicket')}
        </Button>
        {/* 依頼者へ差戻し ⇄ 調査再開のトグル（escalate ⇄ de-escalate と同じパターン）。差戻しは state を
            blocked にし起票スレッドへメッセージを返信する。blocked 中は再送不可＝「調査を再開」になり、
            投稿は無く state を investigating に戻すだけ。どちらも state を変えるため legacy では無効化する。 */}
        {state === 'blocked' ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!editable || isStatePending}
            onClick={() => {
              onApplyState('investigating');
            }}
          >
            {t('ops.detail.sendBack.resume')}
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!editable || state === 'closed'}
            onClick={onSendBack}
          >
            {t('ops.detail.sendBack.button')}
          </Button>
        )}
        {isEscalated ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!editable || !canEscalate || isDeescalatePending}
            title={canEscalate ? undefined : t('ops.detail.escalate.onlyAssignee')}
            onClick={onDeescalate}
          >
            {isDeescalatePending
              ? t('ops.detail.escalate.deescalating')
              : t('ops.detail.escalate.deescalateLabel')}
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!editable || !canEscalate || isEscalatePending}
            title={canEscalate ? undefined : t('ops.detail.escalate.onlyAssignee')}
            onClick={onOpenConfirm}
          >
            {isEscalatePending
              ? t('ops.detail.escalate.escalating')
              : t('ops.detail.escalate.label')}
          </Button>
        )}
      </div>

      {/* Firestore 発番なのに担当外で Escalate が押せない理由を、tooltip 頼みにせず明示する。 */}
      {editable && !canEscalate && (
        <p className="text-xs text-[var(--color-text-muted)]">
          {t('ops.detail.escalate.onlyAssignee')}
        </p>
      )}

      {/* エスカレーションのインライン確認（CTO を ping するため確定を挟む）。 */}
      {confirmingEscalate && (
        <AccentBox tone="warning" title={t('ops.detail.escalate.label')}>
          <div className="space-y-2">
            <p className="text-sm text-[var(--color-text-secondary)]">
              {t('ops.detail.escalate.confirmBody')}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={isEscalatePending}
                onClick={onEscalateConfirmed}
              >
                {isEscalatePending
                  ? t('ops.detail.escalate.escalating')
                  : t('ops.detail.escalate.confirmCta')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={isEscalatePending}
                onClick={() => {
                  setConfirmingEscalate(false);
                }}
              >
                {t('ops.detail.escalate.cancel')}
              </Button>
            </div>
            {/* 失敗時は確認ボックスが開いたまま（retry 可能）なので、エラーはこの中に出す。 */}
            {isEscalateError && (
              <p className="text-sm text-[var(--color-error)]">{t('ops.detail.escalate.failed')}</p>
            )}
          </div>
        </AccentBox>
      )}

      {/* 成功時は確認ボックスを閉じる（onSuccess）ので、成功表示はボックスの外に出す。
          中にネストすると閉じると同時にアンマウントされて一度も見えない。 */}
      {isEscalateSuccess && (
        <p className="text-sm text-[var(--color-success)]">{t('ops.detail.escalate.done')}</p>
      )}

      {/* De-escalate は確認を挟まない直接 undo なので、結果だけをその場に短く出す。 */}
      {isDeescalateSuccess && (
        <p className="text-sm text-[var(--color-success)]">
          {t('ops.detail.escalate.deescalateDone')}
        </p>
      )}
      {isDeescalateError && (
        <p className="text-sm text-[var(--color-error)]">
          {t('ops.detail.escalate.deescalateFailed')}
        </p>
      )}
    </>
  );
}
